import React, { useState, useEffect, useRef, use } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "../api/axios";

// Fixed Rich Text Editor
const RichTextEditor = ({ value, onChange, placeholder }) => {
  const editorRef = useRef(null);
  const [activeCommands, setActiveCommands] = useState({});

  useEffect(() => {
    if (editorRef.current && value) {
      editorRef.current.innerHTML = value;
    }
  }, []);

  const handleCommand = (command) => {
    document.execCommand(command, false, null);
    editorRef.current.focus();
    updateActiveCommands();
    onChange(editorRef.current.innerHTML);
  };

  const updateActiveCommands = () => {
    setActiveCommands({
      bold: document.queryCommandState("bold"),
      italic: document.queryCommandState("italic"),
      underline: document.queryCommandState("underline"),
      insertUnorderedList: document.queryCommandState("insertUnorderedList"),
      insertOrderedList: document.queryCommandState("insertOrderedList"),
    });
  };

  const handleInput = () => {
    onChange(editorRef.current.innerHTML);
    updateActiveCommands();
  };

  const buttonClass = (command) =>
    `btn btn-sm me-1 ${activeCommands[command] ? "btn-primary" : "btn-outline-secondary"}`;

  return (
    <div className="mb-3">
      <div className="mb-2 p-2 border rounded bg-light d-flex flex-wrap">
        <button type="button" className={buttonClass("bold")} onClick={() => handleCommand("bold")}>B</button>
        <button type="button" className={buttonClass("italic")} onClick={() => handleCommand("italic")}>I</button>
        <button type="button" className={buttonClass("underline")} onClick={() => handleCommand("underline")}>U</button>
        <button type="button" className={buttonClass("insertUnorderedList")} onClick={() => handleCommand("insertUnorderedList")}>• List</button>
        <button type="button" className={buttonClass("insertOrderedList")} onClick={() => handleCommand("insertOrderedList")}>1. List</button>
      </div>

      <div
        ref={editorRef}
        contentEditable
        className="border p-2 rounded"
        style={{ minHeight: "150px", backgroundColor: "#fff", direction: "ltr", textAlign: "left", unicodeBidi: "plaintext", overflowY: "auto" }}
        onInput={handleInput}
        onKeyUp={updateActiveCommands}
        onMouseUp={updateActiveCommands}
        data-placeholder={placeholder}
        suppressContentEditableWarning={true}
      />
    </div>
  );
};

// Product Form
const ProductForm = () => {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(false);

  const [form, setForm] = useState({
    name: "",
    brand: "",
    description: "",
    more_description: "",
    other_details: "",
    price: "",
    stock: "",
    category_id: "",
    subcategory_id: "",
    variants: [{ name: "", price: "" }],
    faqs: [{ question: "", answer: "" }],
  });

  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    axios.get("/admin/categories").then((res) => setCategories(res.data));

    if (isEdit) {
      axios.get(`/admin/products/${id}`).then((res) => {
        setForm({
          ...res.data,
          variants: res.data.variants?.length ? res.data.variants : [{ name: "", price: "" }],
          faqs: res.data.faqs?.length ? res.data.faqs : [{ question: "", answer: "" }],
        });
        if (res.data.image_url) setImagePreview(res.data.image_url);
      });
    }
  }, [id, isEdit]);

useEffect(() => {
    const fetchBrands = async () => {
      try {
        const res = await axios.get("/admin/brands");
        setBrands(res.data);
      } catch (err) {
        console.error("Error fetching brands:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchBrands();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: value,
      ...(name === "category_id" ? { subcategory_id: "" } : {}),
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    setImage(file);
    setImagePreview(URL.createObjectURL(file));
  };

  // FAQ handlers
  const handleFAQChange = (index, field, value) => {
    const newFaqs = [...form.faqs];
    newFaqs[index][field] = value;
    setForm({ ...form, faqs: newFaqs });
  };

  const addFAQ = () => setForm({ ...form, faqs: [...form.faqs, { question: "", answer: "" }] });
  const removeFAQ = (index) => {
    const newFaqs = form.faqs.filter((_, i) => i !== index);
    setForm({ ...form, faqs: newFaqs.length ? newFaqs : [{ question: "", answer: "" }] });
  };

  // Variant handlers
  const handleVariantChange = (index, field, value) => {
    const newVariants = [...form.variants];
    newVariants[index][field] = value;
    setForm({ ...form, variants: newVariants });
  };

  const addVariant = () => setForm({ ...form, variants: [...form.variants, { name: "", price: "" }] });
  const removeVariant = (index) => {
    const newVariants = form.variants.filter((_, i) => i !== index);
    setForm({ ...form, variants: newVariants.length ? newVariants : [{ name: "", price: "" }] });
  };

const handleSubmit = async (e) => {
  e.preventDefault();
  const formData = new FormData();

  // Append normal fields
  Object.keys(form).forEach((key) => {
    if (key === "faqs") {
      form.faqs.forEach((faq, i) => {
        formData.append(`faqs[${i}][question]`, faq.question);
        formData.append(`faqs[${i}][answer]`, faq.answer);
      });
    } else if (key === "variants") {
      form.variants.forEach((variant, i) => {
        formData.append(`variants[${i}][name]`, variant.name);
        formData.append(`variants[${i}][price]`, variant.price);
      });
    } else if (key === "variant_prices") {
      Object.entries(form.variant_prices || {}).forEach(([name, price]) => {
        formData.append(`variant_prices[${name}]`, price);
      });
    } else {
      formData.append(key, form[key]);
    }
  });

  if (image) formData.append("image", image);

  try {
    if (isEdit) {
      formData.append("_method", "PUT");
      await axios.post(`/admin/products/${id}`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      alert("Product updated!");
    } else {
      await axios.post("/admin/products", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      alert("Product created!");
    }
    navigate("/admin/products");
  } catch (err) {
    console.error("Error:", err.response?.data || err.message);
    alert("Something went wrong. Check console.");
  }
};


  return (
    <div className="container mt-5">
      <div className="card shadow">
        <div className="card-header bg-primary text-white">
          <h4 className="mb-0">{isEdit ? "Edit Product" : "Add Product"}</h4>
        </div>
        <div className="card-body">
          <form onSubmit={handleSubmit} encType="multipart/form-data" className="row g-3">

            {/* Name */}
            <div className="col-md-6">
              <label className="form-label">Product Name <span className="text-danger">*</span></label>
              <input type="text" className="form-control" name="name" value={form.name} onChange={handleChange} required />
            </div>

            {/* Brand */}
            <div className="col-md-6">
              <label className="form-label">Brand</label>
              <select className="form-select" name="brand" value={form.brand} onChange={handleChange}>
                <option value="">-- Select Brand --</option>
                {brands.map(brand => <option key={brand.id} value={brand.name}>{brand.name}</option>)}
              </select>
            </div>

            {/* Price */}
            <div className="col-md-3">
              <label className="form-label">Base Price (₹) <span className="text-danger">*</span></label>
              <input type="number" className="form-control" name="price" value={form.price} onChange={handleChange} required />
            </div>

            {/* Stock */}
            <div className="col-md-3">
              <label className="form-label">Stock <span className="text-danger">*</span></label>
              <input type="number" className="form-control" name="stock" value={form.stock} onChange={handleChange} required />
            </div>

            {/* Category */}
            <div className="col-md-6">
              <label className="form-label">Category <span className="text-danger">*</span></label>
              <select className="form-select" name="category_id" value={form.category_id} onChange={handleChange} required>
                <option value="">-- Select Category --</option>
                {categories.filter(c => !c.parent_id).map(cat => <option key={cat.id} value={cat.id}>{cat.name}</option>)}
              </select>
            </div>

            {/* Subcategory */}
            <div className="col-md-6">
              <label className="form-label">Subcategory</label>
              <select className="form-select" name="subcategory_id" value={form.subcategory_id} onChange={handleChange} disabled={!form.category_id}>
                <option value="">-- Select Subcategory --</option>
                {categories.filter(c => parseInt(c.parent_id) === parseInt(form.category_id)).map(sub => <option key={sub.id} value={sub.id}>{sub.name}</option>)}
              </select>
            </div>

            {/* Image */}
            <div className="col-md-6">
              <label className="form-label">Product Image</label>
              <input type="file" className="form-control" accept="image/*" onChange={handleImageChange} />
              {imagePreview && <img src={imagePreview} alt="Preview" className="img-thumbnail mt-2" style={{ width: "120px", height: "120px", objectFit: "cover" }} />}
            </div>

            {/* Description */}
            <div className="col-12">
              <label className="form-label">Description</label>
              <RichTextEditor
                value={form.description}
                onChange={(value) => setForm({ ...form, description: value })}
                placeholder="Enter product description..."
              />
            </div>

            {/* More Description */}
            <div className="col-12">
              <label className="form-label">More Description</label>
              <RichTextEditor
                value={form.more_description}
                onChange={(value) => setForm({ ...form, more_description: value })}
                placeholder="Enter additional details..."
              />
            </div>

            {/* Other Details */}
            <div className="col-12">
              <label className="form-label">Other Details</label>
              <textarea className="form-control" rows="2" name="other_details" value={form.other_details} onChange={handleChange}></textarea>
            </div>

            {/* Variants */}
            <div className="col-12">
              <label className="form-label">Variants</label>
              {form.variants.map((variant, index) => (
                <div key={index} className="mb-2 border p-2 rounded d-flex gap-2 align-items-center">
                  <input type="text" className="form-control" placeholder="Variant Name (e.g., Size L)" value={variant.name} onChange={(e) => handleVariantChange(index, "name", e.target.value)} />
                  <input type="number" className="form-control" placeholder="Price" value={variant.price} onChange={(e) => handleVariantChange(index, "price", e.target.value)} />
                  <button type="button" className="btn btn-sm btn-danger" onClick={() => removeVariant(index)}>Remove</button>
                </div>
              ))}
              <button type="button" className="btn btn-sm btn-primary mt-2" onClick={addVariant}>Add Variant</button>
            </div>

            {/* FAQs */}
            <div className="col-12">
              <label className="form-label">FAQs</label>
              {form.faqs.map((faq, index) => (
                <div key={index} className="mb-2 border p-2 rounded">
                  <input type="text" className="form-control mb-1" placeholder="Question" value={faq.question} onChange={(e) => handleFAQChange(index, "question", e.target.value)} />
                  <input type="text" className="form-control mb-1" placeholder="Answer" value={faq.answer} onChange={(e) => handleFAQChange(index, "answer", e.target.value)} />
                  <button type="button" className="btn btn-sm btn-danger" onClick={() => removeFAQ(index)}>Remove</button>
                </div>
              ))}
              <button type="button" className="btn btn-sm btn-primary mt-2" onClick={addFAQ}>Add FAQ</button>
            </div>

            {/* Submit */}
            <div className="col-12 text-end">
              <button type="submit" className="btn btn-success">{isEdit ? "Update Product" : "Create Product"}</button>
            </div>

          </form>
        </div>
      </div>
    </div>
  );
};

export default ProductForm;
